import * as ENGINE from '@gnsx/genesys.js';
import { startCase } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import * as THREE from 'three';

export const toPathKey = (key: string, category: string) => category ? `${category}.${key}` : key;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Tokens produced by `startCase` that should render as acronyms or fixed casing.
 * Regex is derived from keys so map and pattern cannot drift.
 */
const DISPLAY_CASE_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['3 D', '3D'],
  ['Ccd', 'CCD'],
  ['Csm', 'CSM'],
  ['Fps', 'FPS'],
  ['Gltf', 'GLTF'],
  ['Id', 'ID'],
  ['Npc', 'NPC'],
  ['Ui', 'UI'],
  ['Url', 'URL'],
  ['Uuid', 'UUID'],
  ['Uv', 'UV'],
  ['Vfx', 'VFX'],
  ['Vr', 'VR'],
];

const DISPLAY_CASE_MAP = new Map<string, string>(DISPLAY_CASE_PAIRS);

const DISPLAY_CASE_REGEX = new RegExp(
  `(${[...DISPLAY_CASE_MAP.keys()]
    .sort((a, b) => b.length - a.length || a.localeCompare(b))
    .map(escapeRegExp)
    .join('|')})`,
  'g',
);

/** Converts a string to display case. */
export function displayCase(str: string): string {
  if (str.length <= 1) {
    return str.toUpperCase();
  }
  return startCase(str).replace(DISPLAY_CASE_REGEX, match => DISPLAY_CASE_MAP.get(match) ?? match);
}

const nameCounters = new Map<string, number>();
const processedUuids = new Map<string, number>();

export const getCustomDisplayName = (object: ENGINE.Actor | ENGINE.SceneComponent): string => {
  if (object instanceof ENGINE.SceneComponent) {
    if (!!object.name) {
      return object.name;
    }
  }
  if (object instanceof ENGINE.Actor) {
    if (!!object.name) {
      return object.name;
    }
  }
  return '';
};

export const setCustomDisplayName = (object: ENGINE.Actor | ENGINE.SceneComponent, name: string) => {
  if (object instanceof ENGINE.SceneComponent) {
    object.name = name;
    return;
  }
  if (object instanceof ENGINE.Actor) {
    object.setName(name);
  }
};



export const replaceInvalidNameCharacters = (name: string): string => {
  return name
    .replace(/[^a-zA-Z0-9_\s-]/g, '') // Remove invalid characters, including periods
    .replace(/^\s+|\s+$/g, '');       // Trim leading/trailing spaces
};

function toDisplayLabelBase(name: string): string {
  const normalized = replaceInvalidNameCharacters(name.replace(/[_-]+/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
  if (isEmpty(normalized)) {
    return '';
  }
  return displayCase(normalized);
}

/**
 * Converts a display label into a filesystem-safe asset suggestion.
 * Spaces are converted to underscores and invalid characters are removed.
 */
export function suggestAssetFileNameFromDisplayName(name: string): string {
  return name
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .replace(/^_+|_+$/g, '');
}

/** Reset actor name counters */
export const resetActorNameCounters = () => {
  nameCounters.clear();
  processedUuids.clear();
};

/** Get the display name of a class */
export const getClassDisplayName = (object: ENGINE.Actor | ENGINE.SceneComponent, useDisplayCase: boolean = false): string => {
  const className = ENGINE.ClassRegistry.getClassOrConstructorName(object);
  if (useDisplayCase) {
    return displayCase(className);
  }
  return className;
};

/** Generate a default display name for an actor */
export const getActorDisplayName = (actor: ENGINE.Actor, useStartCase: boolean = false): string => {
  const customDisplayName = getCustomDisplayName(actor);
  if (customDisplayName) return customDisplayName;

  let baseName: string = '';

  // Use the model filename when the actor loads a model mesh
  if (actor instanceof ENGINE.ModelMeshActor) {
    let modelUrl: string | null = null;
    const rootComp = actor.rootComponent;
    if (rootComp instanceof ENGINE.ModelMeshComponent) {
      modelUrl = rootComp.modelUrl;
    }
    if (modelUrl) {
      baseName = getFilenameWithoutExt(modelUrl);
    }
  }

  if (!baseName) {
    const rootComponent = actor.rootComponent;
    if (actor.constructor === ENGINE.Actor) {
      const rootComponentName = rootComponent && rootComponent.constructor !== ENGINE.SceneComponent ? findActorDisplayNameFromComponent(rootComponent) : undefined;
      baseName = rootComponentName ?? actor.name;
    } else { // custom actors
      baseName = actor.name;
    }
  }

  const normalizedBaseName = toDisplayLabelBase(baseName) || 'Actor';

  // Check if this UUID has already been processed
  const existingCounter = processedUuids.get(actor.uuid);
  if (existingCounter !== undefined) {
    // Use the existing counter for this UUID
    const resultName = existingCounter > 0
      ? `${normalizedBaseName} ${(existingCounter + 1).toString().padStart(2, '0')}`
      : normalizedBaseName;
    return useStartCase ? startCase(resultName) : resultName;
  }

  // This is a new UUID, assign it the current counter value
  const counter = nameCounters.get(normalizedBaseName) ?? 0;
  const resultName = counter > 0
    ? `${normalizedBaseName} ${(counter + 1).toString().padStart(2, '0')}`
    : normalizedBaseName;

  // Store the counter for this UUID and increment for next time
  processedUuids.set(actor.uuid, counter);
  nameCounters.set(normalizedBaseName, counter + 1);

  return useStartCase ? startCase(resultName) : resultName;
};

export const getComponentDisplayName = (component: ENGINE.SceneComponent, useStartCase: boolean = false, setNameIfEmpty: boolean = true): string => {
  const customDisplayName = getCustomDisplayName(component);
  if (customDisplayName) return customDisplayName;

  const baseName = ENGINE.ClassRegistry.getClassOrConstructorName(component);
  const displayBaseName = useStartCase ? startCase(baseName) : baseName;

  const actor = component.getActor();
  if (!actor) return displayBaseName;

  // Get all components of the same type from the actor
  const components = actor.getComponents(ENGINE.SceneComponent);

  // First pass: collect all used numbers from custom names
  const usedNumbers = new Set<number>();
  const numberPattern = useStartCase ?
    new RegExp(`^${displayBaseName} (\\d+)$`) :
    new RegExp(`^${displayBaseName}_(\\d+)$`);

  for (const c of components) {
    const customName = getCustomDisplayName(c);
    if (customName) {
      const match = customName.match(numberPattern);
      if (match) {
        usedNumbers.add(parseInt(match[1]));
      }
    }
  }

  // Second pass: count components and find our position
  let componentNumber = 0;
  let foundFirst = false;
  let componentName = '';

  for (let i = 0; i < components.length; i++) {
    const c = components[i];
    if (ENGINE.ClassRegistry.getClassOrConstructorName(c) === baseName) {
      if (c.uuid === component.uuid) {
        if (foundFirst) {
          // Find the lowest available number
          let nextNumber = componentNumber + 1;
          while (usedNumbers.has(nextNumber)) {
            nextNumber++;
          }
          componentName = useStartCase ? `${displayBaseName} ${nextNumber}` : `${displayBaseName}_${nextNumber}`;
        } else {
          // This is the first component of this type
          componentName = displayBaseName;
        }
        break;
      }
      if (!foundFirst) foundFirst = true;
      componentNumber++;
    }
  }

  if (isEmpty(componentName)) {
    componentName = displayBaseName;
  }

  if (setNameIfEmpty) {
    component.name = componentName;
  }

  return componentName;
};

/** Minimal outliner node shape for collecting display names (actors + folders). */
export type OutlinerNameNodeLike = {
  id: string;
  name: string;
  isActorNode: boolean;
  isFolder: boolean;
  children: readonly OutlinerNameNodeLike[];
};

function prefabPathBasename(prefabPath: string): string {
  const withoutExt = prefabPath.endsWith(ENGINE.PREFAB_EXTENSION)
    ? prefabPath.slice(0, -ENGINE.PREFAB_EXTENSION.length)
    : prefabPath;
  const parts = withoutExt.split(/[/\\]/);
  return parts.pop() ?? withoutExt;
}

/**
 * Collects actor and folder display names from the outliner tree (same scope as rename batch).
 * Omits nodes whose id is in omitNodeId / omitNodeIds so their current label is not treated as a collision.
 */
export function collectOutlinerNames(
  root: OutlinerNameNodeLike | null | undefined,
  options?: { omitNodeId?: string; omitNodeIds?: Iterable<string> },
): string[] {
  const omitted = new Set<string>();
  if (options?.omitNodeId) {
    omitted.add(options.omitNodeId);
  }
  if (options?.omitNodeIds) {
    for (const id of options.omitNodeIds) {
      omitted.add(id);
    }
  }
  const out: string[] = [];
  const walk = (node: OutlinerNameNodeLike) => {
    if ((node.isActorNode || node.isFolder) && node.name && !omitted.has(node.id)) {
      out.push(node.name);
    }
    for (const c of node.children) {
      walk(c);
    }
  };
  if (root) {
    walk(root);
  }
  return out;
}

/**
 * Editor-oriented default label before uniquify: prefab file, GLTF file, asset path hint, or display class name.
 */
export function suggestActorBaseName(actor: ENGINE.Actor, assetPathHint?: string): string {
  if (assetPathHint) {
    const hintBaseName = assetPathHint.endsWith(ENGINE.PREFAB_EXTENSION)
      ? prefabPathBasename(assetPathHint)
      : getFilenameWithoutExt(assetPathHint);
    const n = toDisplayLabelBase(hintBaseName);
    if (!isEmpty(n)) {
      return n;
    }
  }
  if (!actor.name.toLowerCase().startsWith('actor')) {
    const n = toDisplayLabelBase(actor.name);
    if (!isEmpty(n)) {
      return n;
    }
  }
  const prefabPath = actor.getPrefabName();
  if (prefabPath) {
    const n = toDisplayLabelBase(prefabPathBasename(prefabPath));
    if (!isEmpty(n)) {
      return n;
    }
  }
  if (actor instanceof ENGINE.ModelMeshActor) {
    const rootComp = actor.rootComponent;
    if (rootComp instanceof ENGINE.ModelMeshComponent && rootComp.modelUrl) {
      const n = toDisplayLabelBase(getFilenameWithoutExt(rootComp.modelUrl));
      if (!isEmpty(n)) {
        return n;
      }
    }
  }
  const rootComponentBaseName = findActorDisplayNameFromComponent(actor.rootComponent);
  if (actor.name && actor.constructor === ENGINE.Actor && rootComponentBaseName) {
    const n = toDisplayLabelBase(rootComponentBaseName);
    if (!isEmpty(n)) {
      return n;
    }
  }
  const raw = ENGINE.ClassRegistry.getClassOrConstructorName(actor);
  const stripped = raw.replace(/Actor$/, '');
  // Plain `Actor` becomes "" after strip; never use an empty base label.
  const forDisplay = stripped || raw;
  const label = displayCase(forDisplay);
  return isEmpty(label) ? 'Actor' : label;
}

/**
 * Sets actor name to a value unique among outliner actor + folder labels (excluding omitNodeId).
 * Returns the final name. Caller should sync `outliner.getNodeById(actor.uuid)?.setName(result)` when applicable.
 */
export function assignUniqueActorDisplayName(
  actor: ENGINE.Actor,
  rootNode: OutlinerNameNodeLike | null | undefined,
  options?: { omitNodeId?: string; assetPathHint?: string },
): string {
  const existing = collectOutlinerNames(rootNode, { omitNodeId: options?.omitNodeId ?? actor.uuid });
  const base = suggestActorBaseName(actor, options?.assetPathHint);
  const unique = makeUniqueName(base, existing, { generatedSeparator: ' ' });
  setCustomDisplayName(actor, unique);
  return unique;
}

/**
 * Batch uniquify for several new actors (e.g. prefab drop). Omits all batch uuids from the initial name pool, then pushes each assigned name before the next.
 */
export function assignUniqueActorDisplayNames(
  actors: ENGINE.Actor[],
  rootNode: OutlinerNameNodeLike | null | undefined,
  options?: { getAssetPathHint?: (actor: ENGINE.Actor) => string | undefined },
): void {
  if (actors.length === 0) {
    return;
  }
  const batchIds = new Set(actors.map(a => a.uuid));
  const existing = [...collectOutlinerNames(rootNode, { omitNodeIds: batchIds })];
  for (const actor of actors) {
    const hint = options?.getAssetPathHint?.(actor);
    const base = suggestActorBaseName(actor, hint);
    const unique = makeUniqueName(base, existing, { generatedSeparator: ' ' });
    setCustomDisplayName(actor, unique);
    existing.push(unique);
  }
}

/** Base string for a new component’s display name (matches add/duplicate style: class sans “Component”, display case). */
export function suggestComponentBaseName(component: ENGINE.SceneComponent): string {
  const raw = ENGINE.ClassRegistry.getClassOrConstructorName(component);
  const stripped = raw.replace(/Component$/, '');
  const forDisplay = stripped || raw;
  const label = displayCase(forDisplay);
  return isEmpty(label) ? 'Component' : label;
}

type MakeUniqueNameOptions = {
  defaultPadding?: number;
  generatedSeparator?: '' | '_' | '-' | ' ';
};

function parseMakeUniqueNameOptions(
  optionsOrDefaultPadding?: number | MakeUniqueNameOptions,
): Required<MakeUniqueNameOptions> {
  if (typeof optionsOrDefaultPadding === 'number') {
    return {
      defaultPadding: optionsOrDefaultPadding,
      generatedSeparator: '_',
    };
  }

  return {
    defaultPadding: optionsOrDefaultPadding?.defaultPadding ?? 2,
    generatedSeparator: optionsOrDefaultPadding?.generatedSeparator ?? '_',
  };
}

const getFilenameWithoutExt = (url: string): string => {
  try {
    const decodedURL = decodeURIComponent(url);
    const pathname = new URL(decodedURL).pathname;
    const filename = pathname.split('/').pop() ?? ''; // Extract filename
    return filename.replace(/\.[^/.]+$/, ''); // Remove extension
  } catch (error) {
    const parts = url.split(/[/\\]/);
    const filename = parts.pop() ?? '';
    return filename.replace(/\.[^/.]+$/, '');
  }
};

const findActorDisplayNameFromComponent = (component: ENGINE.SceneComponent): string | null => {
  if (!component || component.constructor === ENGINE.SceneComponent) {
    return null;
  }

  const toDisplayName = (raw: string) => {
    const stripped = raw.replace(/Component$/, '').replace(/Geometry$/, '');
    const displayLabel = displayCase(stripped || raw);
    return isEmpty(displayLabel) ? null : displayLabel;
  };

  if (component instanceof ENGINE.MeshComponent) {
    const geometry = component.geometry;
    if (geometry.constructor === THREE.BufferGeometry) return null;
    return toDisplayName(ENGINE.ClassRegistry.getClassOrConstructorName(geometry));
  }
  return toDisplayName(ENGINE.ClassRegistry.getClassOrConstructorName(component));
};

/**
 * Makes a unique name by checking against existing names.
 *
 * Behavior:
 * - If input name is unique, returns it as-is
 * - If input has numeric suffix (golder01, golder_01, golder-01, golder 01), increments it preserving format
 * - Otherwise appends _02, _03, etc.
 * - Always fills gaps in numbering sequence
 * - Performs case-insensitive comparison
 *
 * Examples:
 * - "name" (unique) → "name"
 * - "name" (conflict) → "name_02"
 * - "golder01" (conflict) → "golder02"
 * - "golder-01" (conflict) → "golder-02"
 * - "golder 01" (conflict) → "golder 02"
 * - "item_005" (conflict) → "item_006"
 * - With existing ["name", "name_02", "name_04"], "name" → "name_03" (fills gap)
 */
export const makeUniqueName = (
  name: string,
  existingNames: string[],
  optionsOrDefaultPadding?: number | MakeUniqueNameOptions,
): string => {
  if (isEmpty(name)) return name;
  const { defaultPadding, generatedSeparator } = parseMakeUniqueNameOptions(optionsOrDefaultPadding);

  // If the exact name is unique (case-insensitive), return it as-is
  if (!existingNames.some(existing => existing.toLowerCase() === name.toLowerCase())) {
    return name;
  }

  // Check if input name has a numeric suffix (golder01, golder_01, golder 01, etc.)
  const inputSuffixRegex = /^(.+?)([_\s-]?)(\d+)$/;
  const inputMatch = name.match(inputSuffixRegex);

  if (inputMatch) {
    // Input has numeric suffix - increment it intelligently
    const baseName = inputMatch[1];
    const separator = inputMatch[2];
    const currentNum = parseInt(inputMatch[3], 10);
    const numPadding = inputMatch[3].length;

    // Look for existing names with same base and separator pattern
    const escapedBaseName = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const baseRegex = new RegExp(`^${escapedBaseName}${escapedSeparator}(\\d+)$`, 'i');
    const usedNumbers = new Set<number>();

    existingNames.forEach(existing => {
      const match = existing.match(baseRegex);
      if (match) {
        usedNumbers.add(parseInt(match[1], 10));
      }
    });

    // Find next available number starting from current + 1
    let nextNum = currentNum + 1;
    while (usedNumbers.has(nextNum)) {
      nextNum++;
    }

    const paddedNum = nextNum.toString().padStart(Math.max(defaultPadding, numPadding), '0');
    const normalizedSeparator = generatedSeparator === '_' && separator === '' ? '' : generatedSeparator;
    return `${baseName}${normalizedSeparator}${paddedNum}`;
  }

  // Input has no numeric suffix - append one
  // Look for existing names that follow the pattern: name, name_01, name_02, etc.
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`^${escapedName}([_\\s-]?)(\\d+)$`, 'i');
  const usedNumbers = new Set<number>();
  let maxPad = defaultPadding;

  existingNames.forEach(existing => {
    // Check for numbered variants: name_01, name-01, name01, etc.
    const match = existing.match(regex);
    if (match) {
      const num = parseInt(match[2], 10);
      usedNumbers.add(num);
      if (match[2].length > maxPad) maxPad = match[2].length;
    }
  });

  // In display-name mode, also consider compact legacy variants such as `GLTFMeshActor_2`.
  if (generatedSeparator === ' ') {
    const compactBase = name.replace(/[\s_-]+/g, '').toLowerCase();
    const numericSuffixRegex = /^(.+?)([_\s-]?)(\d+)$/;
    existingNames.forEach(existing => {
      const match = existing.match(numericSuffixRegex);
      if (!match) {
        return;
      }
      const compactExistingBase = match[1].replace(/[\s_-]+/g, '').toLowerCase();
      if (compactExistingBase !== compactBase) {
        return;
      }
      const num = parseInt(match[3], 10);
      usedNumbers.add(num);
      if (match[3].length > maxPad) maxPad = match[3].length;
    });
  }

  // Find the first available number starting from 2
  let nextNum = 2;
  while (usedNumbers.has(nextNum)) {
    nextNum++;
  }

  // Pad the number and return with configured separator.
  const paddedNum = nextNum.toString().padStart(maxPad, '0');
  return `${name}${generatedSeparator}${paddedNum}`;
};

/**
 * Joins paths together, trimming leading/trailing slashes and normalizing multiple slashes. TODO: Maybe not needed but this checks a few more cases than AssetPath's join
 * Examples:
 * joinPaths('a', 'b', 'c')         // -> 'a/b/c'
 * joinPaths('a/', '/b/', '/c')     // -> 'a/b/c'
 * joinPaths('a', '..', 'c')        // -> 'c'
 * joinPaths('a', 'b', '..', 'c')   // -> 'a/c'
 * joinPaths('/a', 'b')             // -> '/a/b'  (preserves leading slash)
 * joinPaths('a//b')                // -> 'a/b'   (normalizes multiple slashes)
 */
export function joinPaths(...segments: string[]): string {
  // Filter out empty segments and normalize slashes
  const cleanedSegments = segments
    .filter(seg => seg !== '' && seg !== null && seg !== undefined)
    .map(seg =>
      seg
        .replace(/^\/+|\/+$/g, '') // Remove leading/trailing slashes
        .replace(/\/+/g, '/')      // Replace multiple slashes with single
    )
    .filter(seg => seg !== '.' && seg !== ''); // Remove '.' and empty segments

  // Handle empty input
  if (cleanedSegments.length === 0) {
    return '';
  }

  // Process segments to handle '..' for parent directories
  const resolvedSegments: string[] = [];
  for (const seg of cleanedSegments) {
    if (seg === '..') {
      // Remove last segment if it exists and isn't '..'
      if (resolvedSegments.length > 0 && resolvedSegments[resolvedSegments.length - 1] !== '..') {
        resolvedSegments.pop();
      } else {
        // If no segments to pop or last segment is '..', keep it
        resolvedSegments.push(seg);
      }
    } else {
      resolvedSegments.push(seg);
    }
  }

  // Join segments with single slash
  const result = resolvedSegments.join('/');

  // If input started with a slash and result is non-empty, add leading slash
  if (segments[0]?.startsWith('/') && result) {
    return '/' + result;
  }

  return result;
}